// CIP fork feature (see FORK.md): the presence heartbeat for one project.
//
// Mounted by the authenticated layout around every page. Inside a project it
// keeps one heartbeat going for this tab: every 10s while visible, every 4s
// while a teammate is active on the same page (so their markers move
// promptly), every 30s while the tab is hidden, and at once when something a
// peer would see changes (the page, the tab's visibility, the focused card,
// the start of an edit). Leaving the project or closing the tab sends a last
// beat marking the tab gone, so it stays visible, dimmed, as "recently here".
//
// The external systems, all owned by the one effect in `PresenceSession`: the
// timer, the document's input and visibility events, Next's router events,
// and the network. It renders nothing reactive itself; the store carries the
// answers to whichever component subscribes.
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import { nanoid } from "nanoid";
import { useStore } from "zustand";
import { api } from "@/src/utils/api";
import {
  BEAT_MS,
  FAST_BEAT_MS,
  HIDDEN_BEAT_MS,
  peersFrom,
  projectIdOf,
  sectionTitle,
  statusOf,
  type Peer,
  type PresenceBeat,
} from "../lib/presence";
import {
  createPresenceRoom,
  type PresenceRoom,
  type PresenceRoomState,
} from "../store/presenceRoom";

type PresenceContextValue = {
  room: PresenceRoom;
  projectId: string;
  selfUserId: string | null;
  /** How this person is named to others (shared-text carets). */
  selfName: string;
  clientId: string;
};

const PresenceContext = createContext<PresenceContextValue | null>(null);

const CLIENT_ID_KEY = "cip-presence-client-id";

/** One id per tab that survives a reload, so reloading does not leave a
 * dimmed ghost of yourself behind for everyone else. */
function tabClientId(): string {
  const fresh = () =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : nanoid();
  try {
    const stored = window.sessionStorage.getItem(CLIENT_ID_KEY);
    if (stored && stored.length >= 8) return stored;
    const id = fresh();
    window.sessionStorage.setItem(CLIENT_ID_KEY, id);
    return id;
  } catch {
    return fresh();
  }
}

export function PresenceProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const projectId =
    typeof router.query.projectId === "string"
      ? router.query.projectId
      : projectIdOf(router.asPath);
  if (!projectId) return <>{children}</>;
  // Keyed: switching project ends one session (with its goodbye beat) and
  // starts the next from nothing.
  return (
    <PresenceSession key={projectId} projectId={projectId}>
      {children}
    </PresenceSession>
  );
}

function PresenceSession({
  projectId,
  children,
}: {
  projectId: string;
  children: ReactNode;
}) {
  const router = useRouter();
  const session = useSession();
  const utils = api.useUtils();
  // Read through refs: the heartbeat runs for the whole project session and
  // must not restart because either object's identity changed.
  const utilsRef = useRef(utils);
  utilsRef.current = utils;
  const routerRef = useRef(router);
  routerRef.current = router;
  const [room] = useState(createPresenceRoom);
  const [clientId] = useState(tabClientId);
  const selfUserId = session.data?.user?.id ?? null;
  const selfName =
    session.data?.user?.name ?? session.data?.user?.email ?? "Someone";

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let soon: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;
    let again = false;
    let stopped = false;
    let lastInputAt = Date.now();

    const payload = (leaving = false): PresenceBeat => {
      const { detail, editingUntil } = room.getState();
      const href = routerRef.current.asPath;
      const path = href.split(/[?#]/)[0] ?? href;
      return {
        projectId,
        clientId,
        location: {
          path,
          href,
          title: detail?.title ?? sectionTitle(path),
          resource: detail?.resource ?? null,
          view: detail?.view ?? null,
        },
        focus: detail?.focus ?? null,
        editing: Date.now() < editingUntil,
        visible: document.visibilityState === "visible",
        idleMs: Math.max(0, Date.now() - lastInputAt),
        leaving,
        sentAt: Date.now(),
      };
    };

    /** The next tick: slower when hidden, faster beside an active peer. */
    const interval = () => {
      if (document.visibilityState !== "visible") return HIDDEN_BEAT_MS;
      const { entries } = room.getState();
      const here = payload().location;
      const besideSomeone = entries.some(
        (entry) =>
          entry.userId !== selfUserId &&
          statusOf(entry) === "active" &&
          (entry.location.path === here.path ||
            (here.resource &&
              entry.location.resource?.id === here.resource.id)),
      );
      return besideSomeone ? FAST_BEAT_MS : BEAT_MS;
    };

    const schedule = (ms: number) => {
      clearTimeout(timer);
      if (!stopped) timer = setTimeout(beat, ms);
    };

    const beat = () => {
      if (stopped) return;
      if (inFlight) {
        again = true;
        return;
      }
      inFlight = true;
      clearTimeout(timer);
      utilsRef.current.client.presence.beat
        .mutate(payload())
        .then(({ entries }) => {
          if (!stopped) room.getState().actions.received(entries);
        })
        // Presence never interrupts work: a failed beat is retried by the
        // next tick, and nobody is told.
        .catch(() => {})
        .finally(() => {
          inFlight = false;
          if (again) {
            again = false;
            beat();
          } else {
            schedule(interval());
          }
        });
    };

    const beatSoon = () => {
      clearTimeout(soon);
      soon = setTimeout(beat, 250);
    };

    const goodbye = () => {
      // Best effort: the request may not outlive the page.
      utilsRef.current.client.presence.beat
        .mutate(payload(true))
        .catch(() => undefined);
    };

    const onInput = () => {
      const idleBefore = Date.now() - lastInputAt;
      lastInputAt = Date.now();
      // Coming back from idle is news; ordinary input rides the tick.
      if (idleBefore > 30_000) beatSoon();
    };
    const onVisibility = () => beatSoon();

    const unsubscribe = room.subscribe((state, previous) => {
      if (state.changed !== previous.changed) beatSoon();
    });
    const inputEvents = ["pointerdown", "keydown", "wheel", "pointermove"];
    for (const name of inputEvents) {
      window.addEventListener(name, onInput, { passive: true });
    }
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", goodbye);
    const events = routerRef.current.events;
    events.on("routeChangeComplete", beatSoon);
    // Once the session knows who we are, so our own tabs are recognised.
    beat();

    return () => {
      stopped = true;
      clearTimeout(timer);
      clearTimeout(soon);
      unsubscribe();
      for (const name of inputEvents) {
        window.removeEventListener(name, onInput);
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", goodbye);
      events.off("routeChangeComplete", beatSoon);
      goodbye();
    };
    // The session id only changes on sign-in, which remounts the layout.
  }, [room, clientId, projectId, selfUserId]);

  return (
    <PresenceContext.Provider
      value={{ room, projectId, selfUserId, selfName, clientId }}
    >
      {children}
    </PresenceContext.Provider>
  );
}

/** The project's presence, or null outside a project. */
export function usePresenceContext(): PresenceContextValue | null {
  return useContext(PresenceContext);
}

/** Everyone else in the project, as people (tabs collapsed), active first. */
export function usePeers(): Peer[] {
  const context = useContext(PresenceContext);
  const entries = useStore(
    context?.room ?? EMPTY_ROOM,
    (state) => state.entries,
  );
  const self = context?.selfUserId ?? null;
  return useMemo(() => peersFrom(entries, self), [entries, self]);
}

/** The presence room's actions, for pages that describe themselves. */
export function usePresenceActions(): PresenceRoomState["actions"] | null {
  const context = useContext(PresenceContext);
  return context ? context.room.getState().actions : null;
}

const EMPTY_ROOM = createPresenceRoom();
